import React from 'react';
import { Company } from '../types';
import { getAssetUrl } from '../../../utils/api';

interface CompanyInfoSectionProps {
  company: Company | null;
  companyName?: string;
  companyAddress?: string;
  companyCity?: string;
  companyPostalCode?: string;
  companyPhone?: string;
  companyEmail?: string;
  logoPosition: 'top-left' | 'top-right' | 'top-center';
  accentColor?: string;
}

/**
 * Section d'affichage des informations de l'entreprise
 * Supporte 3 positions de logo : gauche, droite, centré
 */
export const CompanyInfoSection: React.FC<CompanyInfoSectionProps> = ({
  company,
  companyName,
  companyAddress,
  companyCity,
  companyPostalCode,
  companyPhone,
  companyEmail,
  logoPosition,
  accentColor = '#0D9488'
}) => {
  const logoUrl = (company as any)?.logoUrl || '';
  const name = (company as any)?.legalName || (company as any)?.tradeName || companyName || '';
  const address = (company as any)?.addressLine1 || companyAddress || '';
  const city = company?.city || companyCity || '';
  const postalCode = (company as any)?.zipCode || companyPostalCode || '';
  const phone = (company as any)?.phone || companyPhone || '';
  const email = (company as any)?.email || companyEmail || '';

  // Déterminer la classe de disposition selon la position du logo
  const getContainerClass = () => {
    switch (logoPosition) {
      case 'top-center':
        return 'flex flex-col items-center text-center';
      case 'top-right':
        return 'flex flex-col md:flex-row-reverse md:justify-between md:items-start gap-4 md:gap-6';
      case 'top-left':
      default:
        return 'flex flex-col md:flex-row md:justify-start md:items-start gap-4 md:gap-6';
    }
  };

  return (
    <div className={getContainerClass()}>
      {/* Logo */}
      {logoUrl && (
        <img
          src={getAssetUrl(logoUrl)}
          alt="Logo"
          className={`
            object-contain flex-shrink-0
            ${logoPosition === 'top-center'
              ? 'w-20 h-20 md:w-32 md:h-32 mx-auto mb-2'
              : 'w-16 h-16 md:w-24 md:h-24'
            }
          `}
          onError={(e) => {
            e.currentTarget.src = '/logo.svg';
          }}
        />
      )}

      {/* Informations entreprise */}
      <div className={logoPosition === 'top-center' ? 'text-center' : ''}>
        {/* Nom de l'entreprise - grand si pas de logo, normal si logo */}
        {!logoUrl && (
          <h2 className="text-xl md:text-3xl font-bold text-gray-800">
            {name}
          </h2>
        )}

        <div className="text-gray-600 mt-1 md:mt-2 space-y-0.5 md:space-y-1 text-xs md:text-sm">
          {/* Nom en taille normale si logo présent */}
          {logoUrl && (
            <p className="font-bold text-gray-800">
              {name}
            </p>
          )}

          {address && <p>{address}</p>}
          {(postalCode || city) && (
            <p>
              {postalCode && `${postalCode} - `}
              {city}
            </p>
          )}
          {phone && <p>{phone}</p>}
          {email && <p style={{ color: accentColor }}>{email}</p>}
        </div>
      </div>
    </div>
  );
};
